import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  addExpense,
  updateExpense,
  clearError,
  fetchExpenses,
} from "../../slice/ExpenseSlice";
import { fetchCategories } from "../../slice/ExpenseCategorySlice";
import NotifyData from "../../components/NotifyData";
import PageTitle from "../../components/PageTitle";

const expenseSchema = [
  { name: "expense_date", label: "Expense Date", type: "date", required: true },
  {
    name: "category_id",
    label: "Category",
    type: "select",
    required: true,
    options: [], // Will be populated dynamically
  },
  {
    name: "description",
    label: "Description",
    type: "text",
    required: false, // Description is optional
  },
  { name: "amount", label: "Amount", type: "text", required: true, min: 0 },
];

const ExpenseCreation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { expenseId } = useParams();
  const {
    expenses,
    error,
    status: expenseStatus,
  } = useSelector((state) => state.expense);
  const { categories, status: categoryStatus } = useSelector(
    (state) => state.categories,
  );
  const isEditMode = !!expenseId;

  const [formData, setFormData] = useState({
    expense_date: "",
    category_id: "",
    category_name: "",
    description: "",
    amount: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [expenseEntries, setExpenseEntries] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    setLoadingCategories(true);
    dispatch(fetchCategories({ searchText: "" })).finally(() => {
      setLoadingCategories(false);
    });

    if (isEditMode && expenses.length > 0) {
      const expense = expenses.find((e) => e.expense_id === expenseId);
      if (expense) {
        const expenseData = expense.expense_data || [];
        setFormData({
          expense_date: expense.expense_date || "",
          category_id: "",
          category_name: "",
          description: "",
          amount: "",
        });
        setExpenseEntries(expenseData);
      }
    } else {
      const today = new Date().toISOString().split("T")[0];
      setFormData((prev) => ({
        ...prev,
        expense_date: today,
      }));
    }
  }, [isEditMode, expenseId, expenses, dispatch]);

  useEffect(() => {
    if (error) {
      NotifyData(error, "error");
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const validateForm = () => {
    const errors = {};
    if (!formData.expense_date) {
      errors.expense_date = "Expense Date is required";
    }
    if (!formData.category_id) {
      errors.category_id = "Category is required";
    }
    if (!formData.amount || formData.amount <= 0) {
      errors.amount = "Amount must be greater than 0";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "category_id") {
      const selectedCategory = categories.find(
        (cat) => cat.category_id === value,
      );
      setFormData({
        ...formData,
        category_id: value,
        category_name: selectedCategory ? selectedCategory.category_name : "",
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setValidationErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleAddRow = () => {
    if (!validateForm()) {
      NotifyData("Please fill in all required fields correctly", "error");
      return;
    }

    const newEntry = {
      category_id: formData.category_id,
      category_name: formData.category_name,
      description: formData.description,
      amount: parseFloat(formData.amount),
    };

    setExpenseEntries([...expenseEntries, newEntry]);

    // Reset form fields (except expense_date) after adding a row
    setFormData({
      ...formData,
      category_id: "",
      category_name: "",
      description: "",
      amount: "",
    });
    setValidationErrors({});
  };

  const handleEditRow = (index) => {
    const entry = expenseEntries[index];
    setFormData({
      ...formData,
      category_id: entry.category_id,
      category_name: entry.category_name,
      description: entry.description,
      amount: entry.amount,
    });

    // Remove the edited row from the table
    setExpenseEntries(expenseEntries.filter((_, i) => i !== index));
  };

  const handleDeleteRow = (index) => {
    setExpenseEntries(expenseEntries.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let entriesToSubmit = [...expenseEntries];

    if (entriesToSubmit.length === 0) {
      if (!validateForm()) {
        NotifyData("Please fill in all required fields correctly", "error");
        return;
      }
      entriesToSubmit = [
        {
          category_id: formData.category_id,
          category_name: formData.category_name,
          description: formData.description,
          amount: parseFloat(formData.amount),
        },
      ];
    }

    if (entriesToSubmit.length === 0) {
      NotifyData("Please add at least one expense entry", "error");
      return;
    }

    const expenseData = {
      expense_date: formData.expense_date,
      expense_data: entriesToSubmit,
    };

    try {
      dispatch(clearError());

      if (isEditMode) {
        await dispatch(
          updateExpense({ expense_id: expenseId, ...expenseData }),
        ).unwrap();
        NotifyData("Expense Updated Successfully", "success");
      } else {
        await dispatch(addExpense(expenseData)).unwrap();
        NotifyData("Expense Created Successfully", "success");
        await dispatch(fetchExpenses({ searchText: "" })).unwrap();
      }

      navigate("/expense");
    } catch (err) {
      console.error("Submission Error:", err);
    }
  };

  const updatedSchema = expenseSchema.map((field) => {
    if (field.name === "category_id") {
      return {
        ...field,
        options: categories.map((cat) => ({
          value: cat.category_id,
          label: cat.category_name,
        })),
      };
    }
    return field;
  });

  return (
    <div id="main">
      <Container>
        <Row>
          <Col xs="12" className="py-3">
            <PageTitle
              PageTitle={isEditMode ? "Edit Expense" : "Expense Creation"}
              showButton={false}
            />
          </Col>
          <Col xs="12" className="py-3">
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                {updatedSchema.map((field, index) => (
                  <Col lg="3" md="6" className="mb-3" key={index}>
                    <Form.Group>
                      <Form.Label>{field.label}</Form.Label>
                      {field.type === "select" ? (
                        <Form.Select
                          name={field.name}
                          value={formData[field.name] || ""}
                          onChange={handleChange}
                          isInvalid={!!validationErrors[field.name]}
                          disabled={loadingCategories} // Disable dropdown while loading
                        >
                          <option value="">
                            {loadingCategories
                              ? "Loading Categories..."
                              : "Select " + field.label}
                          </option>
                          {field.options.map((option, idx) => (
                            <option key={idx} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          type={field.type}
                          name={field.name}
                          value={formData[field.name] || ""}
                          onChange={handleChange}
                          placeholder={field.label}
                          required={field.required}
                          min={field.min}
                          isInvalid={!!validationErrors[field.name]}
                        />
                      )}
                      <Form.Control.Feedback type="invalid">
                        {validationErrors[field.name]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                ))}
              </Row>
              <Row>
                <Col xs="12" className="mb-3">
                  <Button
                    variant="danger"
                    onClick={handleAddRow}
                    disabled={loadingCategories}
                  >
                    Add Row
                  </Button>
                </Col>
              </Row>

              {expenseEntries.length > 0 && (
                <Row>
                  <Col xs="12" className="mb-3">
                    <h5>Expense Entries</h5>
                    <Table bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Amount</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenseEntries.map((entry, index) => (
                          <tr key={index}>
                            <td>{entry.category_name || "N/A"}</td>
                            <td>{entry.description || "N/A"}</td>
                            <td>{entry.amount}</td>
                            <td>
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => handleEditRow(index)}
                                className="me-2"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteRow(index)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              )}

              <div className="text-center mt-3">
                <Button
                  variant="success"
                  className="mx-3"
                  type="submit"
                  disabled={loadingCategories}
                >
                  {isEditMode ? "Update" : "Submit"}
                </Button>{" "}
                <Button
                  variant="secondary"
                  className="mx-3"
                  onClick={() => navigate("/expense")}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ExpenseCreation;
